import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { Search as SearchIcon } from "@mui/icons-material";
import { useAuth } from "../contexts/AuthContext";
import { apiFetch } from "../lib/api";

// Types for admin reviews API
interface ApiUser {
  id: string;
  name: string;
  email: string;
  role: string; // "driver" | "rider" | "admin"
}

interface ApiReview {
  id: string;
  giver: ApiUser;
  about: ApiUser;
  rating: number;
  comment?: string | null;
  createdAt: string; // ISO
}

interface ApiResponse<T> {
  success: boolean;
  data: T;
  pagination?: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

// UI row model for the grid
interface UIReviewRow {
  id: string;
  dateText: string;
  timeText: string;
  reviewerName: string;
  reviewerRole: string;
  receiverName: string;
  receiverRole: string;
  rating: number;
  commentText: string;
  createdAtIso: string;
}

const Reviews: React.FC = () => {
  const { token } = useAuth();
  const [rows, setRows] = useState<UIReviewRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");

  const formatDate = (iso: string) =>
    new Date(iso).toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata" });

  const formatTimeIST = (iso?: string | null) => {
    if (!iso) return "-";
    return new Date(iso).toLocaleTimeString("en-IN", {
      timeZone: "Asia/Kolkata",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const statusPill = (
    label: string,
    type: "primary" | "success" | "error" = "primary"
  ) => {
    const bg: Record<typeof type, string> = {
      primary: "#14b8a6",
      success: "#10b981",
      error: "#ef4444",
    } as const;
    return (
      <Box
        component="span"
        sx={{
          display: "inline-block",
          px: 1,
          py: 0.25,
          borderRadius: 16,
          fontSize: 12,
          lineHeight: 1.4,
          fontWeight: 500,
          color: "#fff",
          backgroundColor: bg[type],
        }}
      >
        {label}
      </Box>
    );
  };

  const getRoleChip = useCallback((role: string) => {
    const map: Record<string, { type: "primary" | "success" | "error"; label: string }> = {
      rider: { type: "primary", label: "Rider" },
      driver: { type: "success", label: "Driver" },
      admin: { type: "error", label: "Admin" },
    };
    const cfg = map[role] || { type: "primary", label: role };
    return statusPill(cfg.label, cfg.type);
  }, []);

  useEffect(() => {
    let isMounted = true;
    const fetchReviews = async () => {
      setLoading(true);
      try {
        const res = await apiFetch(`/admin/reviews?page=1&limit=100`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: token ? `Bearer ${token}` : "",
          },
        });
        const json: ApiResponse<ApiReview[]> = await res.json();
        if (json.success && Array.isArray(json.data)) {
          const uiRows: UIReviewRow[] = json.data.map((r) => ({
            id: r.id,
            dateText: formatDate(r.createdAt),
            timeText: formatTimeIST(r.createdAt),
            reviewerName: r.giver?.name || "-",
            reviewerRole: r.giver?.role || "-",
            receiverName: r.about?.name || "-",
            receiverRole: r.about?.role || "-",
            rating: r.rating,
            commentText: r.comment || "-",
            createdAtIso: r.createdAt,
          }));
          if (isMounted) setRows(uiRows);
        } else {
          if (isMounted) setRows([]);
        }
      } catch (e) {
        if (isMounted) setRows([]);
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    if (token) fetchReviews();
    return () => {
      isMounted = false;
    };
  }, [token]);

  const columns: GridColDef<UIReviewRow>[] = useMemo(
    () => [
      // hidden raw timestamp field for proper sorting by most recent first
      { field: "createdAtIso", hide: true, sortable: true, filterable: false, disableColumnMenu: true },
      { field: "dateText", headerName: "Date", width: 100, flex: 0.6 },
      { field: "reviewerName", headerName: "Reviewer", width: 140, flex: 0.8 },
      {
        field: "reviewerRole",
        headerName: "Reviewer Role",
        width: 120,
        flex: 0.7,
        renderCell: (params) => getRoleChip(String(params.value || "")),
      },
      { field: "receiverName", headerName: "Reviewee", width: 140, flex: 0.8 },
      {
        field: "receiverRole",
        headerName: "Reviewee Role",
        width: 120,
        flex: 0.7,
        renderCell: (params) => getRoleChip(String(params.value || "")),
      },
      { field: "rating", headerName: "Rating", width: 80, flex: 0.5 },
      { field: "commentText", headerName: "Comment", flex: 1.5, minWidth: 200 },
      { field: "timeText", headerName: "Rating Time", width: 120, flex: 0.7 },
    ],
    [getRoleChip]
  );

  const filteredRows = rows.filter((row) => {
    const term = searchTerm.toLowerCase();
    const matchesSearch =
      row.reviewerName.toLowerCase().includes(term) ||
      row.receiverName.toLowerCase().includes(term) ||
      row.commentText.toLowerCase().includes(term);

    const matchesRole =
      roleFilter === "all" ||
      row.reviewerRole === roleFilter ||
      row.receiverRole === roleFilter;

    return matchesSearch && matchesRole;
  });

  return (
    <Box>
      {/* Header Section with Gradient Background */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #14b8a6 0%, #0f766e 100%)',
          color: 'white',
          p: 4,
          borderRadius: 2,
          mb: 3,
          boxShadow: '0 8px 32px rgba(20, 184, 166, 0.3)',
        }}
      >
        <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', mb: 1 }}>
          Reviews Management
        </Typography>
        <Typography variant="body1" sx={{ opacity: 0.9 }}>
          Monitor user feedback and ratings across the platform
        </Typography>
      </Box>

      <Paper 
        sx={{ 
          p: 2, 
          mb: 3,
          background: 'linear-gradient(135deg, #f0fdfa 0%, #ecfdf5 100%)',
          border: '1px solid #10b98130',
          borderRadius: 2,
        }}
      >
        <Box
          sx={{
            display: "flex",
            gap: 2,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <TextField
            placeholder="Search reviews..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              },
            }}
            sx={{ flexGrow: 1, minWidth: 300 }}
          />
          <FormControl sx={{ minWidth: 180 }}>
            <InputLabel>Role</InputLabel>
            <Select
              value={roleFilter}
              label="Role"
              onChange={(e) => setRoleFilter(e.target.value)}
            >
              <MenuItem value="all">All Roles</MenuItem>
              <MenuItem value="rider">Rider</MenuItem>
              <MenuItem value="driver">Driver</MenuItem>
              <MenuItem value="admin">Admin</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Paper>

      <Paper sx={{ height: 600, width: "100%", overflow: 'hidden' }}>
        <DataGrid
          rows={filteredRows}
          columns={columns}
          pageSizeOptions={[5, 10, 25]}
          initialState={{
            columns: {
              columnVisibilityModel: {
                createdAtIso: false,
              },
            },
            pagination: {
              paginationModel: { page: 0, pageSize: 10 },
            },
            sorting: {
              sortModel: [
                { field: "createdAtIso", sort: "desc" as const },
              ],
            },
          }}
          getRowId={(row) => row.id}
          loading={loading}
          disableRowSelectionOnClick
          sx={{
            '& .MuiDataGrid-cell': {
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            },
            '& .MuiDataGrid-columnHeader': {
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            },
          }}
        />
      </Paper>
    </Box>
  );
};

export default Reviews;
